import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger);

/*
 * Declarative scroll animations driven by data attributes:
 *   data-anim="up|down|left|right|zoom|fade|flip"  animate in when entering, out when leaving (both directions)
 *   data-stagger                                    animate the element's children one after another
 *   data-delay="0.2"                                delay in seconds
 *   data-split                                      split headline into words and slide each up
 *   data-parallax="0.3"                             vertical parallax (negative = opposite direction)
 *   data-count="120" data-suffix="+"                count-up number
 *   data-line (inside data-line-wrap)               vertical line that draws with scroll
 */

const FROM = {
  up: { y: 70 },
  down: { y: -70 },
  left: { x: -110 },
  right: { x: 110 },
  zoom: { scale: 0.82 },
  fade: {},
  flip: { y: 50, rotationX: -28, transformPerspective: 900 },
};
const REST = { x: 0, y: 0, scale: 1, rotationX: 0 };

function splitWords(el) {
  if (el.dataset.splitDone) return;
  el.dataset.splitDone = '1';

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 3) {
        const frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) return frag.appendChild(document.createTextNode(' '));
          const outer = document.createElement('span');
          outer.className = 'sw';
          const inner = document.createElement('span');
          inner.textContent = part;
          outer.appendChild(inner);
          frag.appendChild(outer);
        });
        child.replaceWith(frag);
      } else if (child.nodeType === 1 && child.tagName !== 'BR') {
        const carried = ['text-gradient', 'text-outline', 'text-outline-light', 'text-accent', 'text-accent-light'].filter((c) => child.classList.contains(c));
        walk(child);
        carried.forEach((c) => {
          child.classList.remove(c);
          child.querySelectorAll('.sw > span').forEach((s) => s.classList.add(c));
        });
      }
    });
  };
  walk(el);
}

function setupReveal(el) {
  const type = el.dataset.anim in FROM ? el.dataset.anim : 'up';
  const from = { autoAlpha: 0, ...FROM[type] };
  const to = { autoAlpha: 1, ...REST, duration: 0.9, ease: 'power3.out', overwrite: 'auto' };
  const out = { ...from, duration: 0.5, ease: 'power2.in', overwrite: 'auto' };
  const delay = parseFloat(el.dataset.delay || 0);

  if (el.hasAttribute('data-stagger')) {
    const kids = Array.from(el.children);
    gsap.set(kids, from);
    ScrollTrigger.batch(kids, {
      start: 'top 92%',
      end: 'bottom top',
      onEnter: (b) => gsap.to(b, { ...to, stagger: 0.12, delay }),
      onEnterBack: (b) => gsap.to(b, { ...to, stagger: 0.1 }),
      onLeave: (b) => gsap.to(b, { ...out, stagger: 0.04 }),
      onLeaveBack: (b) => gsap.to(b, { ...out, stagger: 0.04 }),
    });
    return;
  }

  gsap.set(el, from);
  ScrollTrigger.create({
    trigger: el,
    start: 'top 90%',
    end: 'bottom top',
    onEnter: () => gsap.to(el, { ...to, delay }),
    onEnterBack: () => gsap.to(el, to),
    onLeave: () => gsap.to(el, out),
    onLeaveBack: () => gsap.to(el, out),
  });
}

function setupSplit(el) {
  splitWords(el);
  const words = el.querySelectorAll('.sw > span');
  const delay = parseFloat(el.dataset.delay || 0);
  gsap.set(words, { yPercent: 115 });
  ScrollTrigger.create({
    trigger: el,
    start: 'top 92%',
    end: 'bottom top',
    onEnter: () => gsap.to(words, { yPercent: 0, duration: 0.9, ease: 'power4.out', stagger: 0.05, delay, overwrite: 'auto' }),
    onEnterBack: () => gsap.to(words, { yPercent: 0, duration: 0.8, ease: 'power4.out', stagger: 0.04, overwrite: 'auto' }),
    onLeave: () => gsap.to(words, { yPercent: -115, duration: 0.45, ease: 'power2.in', stagger: 0.02, overwrite: 'auto' }),
    onLeaveBack: () => gsap.to(words, { yPercent: 115, duration: 0.45, ease: 'power2.in', stagger: 0.02, overwrite: 'auto' }),
  });
}

function setupCount(el) {
  const target = parseFloat(el.dataset.count);
  const suffix = el.dataset.suffix || '';
  const state = { v: 0 };
  const render = () => { el.textContent = `${Math.round(state.v)}${suffix}`; };
  render();
  const run = () => gsap.to(state, { v: target, duration: 2.2, ease: 'power2.out', onUpdate: render, overwrite: true });
  const reset = () => { gsap.killTweensOf(state); state.v = 0; render(); };
  ScrollTrigger.create({ trigger: el, start: 'top 92%', end: 'bottom top', onEnter: run, onEnterBack: run, onLeave: reset, onLeaveBack: reset });
}

function setupParallax(el) {
  const speed = parseFloat(el.dataset.parallax || 0.2);
  gsap.fromTo(
    el,
    { y: -speed * 120 },
    { y: speed * 120, ease: 'none', scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } }
  );
}

function setupLine(el) {
  const wrap = el.closest('[data-line-wrap]') || el.parentElement;
  gsap.fromTo(
    el,
    { scaleY: 0, transformOrigin: 'top center' },
    { scaleY: 1, ease: 'none', scrollTrigger: { trigger: wrap, start: 'top 60%', end: 'bottom 60%', scrub: 0.4 } }
  );
}

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Smooth scrolling (Lenis) is created once for the whole app.
function useLenis() {
  useEffect(() => {
    if (reduceMotion()) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    const tick = (t) => lenis.raf(t * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
}

// Re-runs on every route change: jump to top, then wire up the animations for the new page's DOM.
export default function useScrollAnimations(routeKey) {
  useLenis();

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    if (reduceMotion()) return undefined;

    const ctx = gsap.context(() => {
      document.querySelectorAll('[data-split]').forEach(setupSplit);
      document.querySelectorAll('[data-anim]').forEach(setupReveal);
      document.querySelectorAll('[data-count]').forEach(setupCount);
      document.querySelectorAll('[data-parallax]').forEach(setupParallax);
      document.querySelectorAll('[data-line]').forEach(setupLine);

      gsap.to('.scroll-progress', {
        scaleX: 1,
        ease: 'none',
        scrollTrigger: { trigger: document.documentElement, start: 'top top', end: 'bottom bottom', scrub: 0.2 },
      });
    });

    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);

    return () => {
      window.removeEventListener('load', refresh);
      ctx.revert();
    };
  }, [routeKey]);
}

export function scrollToTarget(href) {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
  else el.scrollIntoView({ behavior: 'smooth' });
}

export function refreshScroll(delay = 450) {
  setTimeout(() => ScrollTrigger.refresh(), delay);
}
